/**
 * RunTestcases component is responsible for executing and displaying the status of 
 * both visible and hidden test cases for a student's programming submission. It 
 * interacts with the backend to fetch the execution status of test cases, triggers 
 * their execution, and displays the results.
 *
 * Key Features:
 * - Fetches and displays the execution status of visible and hidden test cases.
 * - Provides buttons to run visible and hidden test cases.
 * - Displays a loading spinner during the test case execution.
 * - Shows the count of passed test cases out of total test cases.
. The buttons to run test cases are conditionally rendered 
 * based on the execution status and access control variables.
 */
import log from "loglevel";

import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import httpService from "services/httpService.js";
import * as constants from "Constants";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import TestCaseModal from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/TestCaseModal.jsx'
// import useFetchData from "hooks/useFetchData";

function RunTestcases(props) {

  log.log("RunTestcases props: ", props)
  const userId = useSelector((state) => state.userInfo.userId);

  // state for execution status of visible test cases
  const [visibleTestcaseExecutionPending, setVisibleTestcaseExecutionPending] =
    useState(true);
  //state for execution status of hidden testcases
  const [hiddenTestcaseExecutionPending, setHiddenTestcaseExecutionPending] =
    useState(true);
  const [toolTipMessageVisible, setToolTipMessageVisible] = useState("Click to run testcases")
  const [toolTipMessageHidden, setToolTipMessageHidden] = useState("Click to run testcases")
  const [visibleTestCaseModal, setVisibleTestCaseModal] = useState(false)
  const [hiddenTestCaseModal, setHiddenTestCaseModal] = useState(false)
  const [studentEmail, setStudentEmail] = useState("")
  const [studentName, setStudentName] = useState("");
  const [testCaseMessage, setTestCaseMessage] = useState("");

  const fetchUserProfile = async (userId) => {
    const response = await httpService.get(`/accounts/api/profile/`);
    const profile = response.data?.[0];
    log.debug("response, profile", profile)
    setStudentEmail(profile?.email);
    setStudentName(`${profile?.first_name} ${profile?.last_name}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchUserProfile(userId);
      } catch (error) {
        log.log(error)
      }
    }
    fetchData();
  }, [])

  // Update execution pending status from selectedSubmission prop
  // This avoids duplicate API call since ManageSubmissions already fetches this data
  const updateExecutionStatusFromSubmission = useCallback(() => {
    if (props.selectedSubmission) {
      if (props.selectedSubmission.execution_pending_for_hidden_testcases === false) {
        setHiddenTestcaseExecutionPending(false);
      }
      if (props.selectedSubmission.execution_pending_for_visible_testcases === false) {
        setVisibleTestcaseExecutionPending(false);
      }
    }
  }, [props.selectedSubmission]);

  // Update execution status when selectedSubmission changes
  useEffect(() => {
    updateExecutionStatusFromSubmission();
  }, [props.selectedSubmission]);

  useEffect(() => {
    if (props?.visibleTestcaseEvaluationResultsStatus === "Evaluation Finished") {
      // Evaluation finished, execution is no longer pending
      setVisibleTestcaseExecutionPending(false);
      runTestCaseButton("visible");
    }
  }, [props.visibleTestcaseEvaluationResultsStatus, props.selectedSubmission]);


  useEffect(() => {
    if (props?.hiddenTestcaseEvaluationResultStatus === "Evaluation Finished") {
      // Evaluation finished, execution is no longer pending
      setHiddenTestcaseExecutionPending(false);
      runTestCaseButton("hidden");
    }
  }, [props.hiddenTestcaseEvaluationResultStatus, props.selectedSubmission]);

  useEffect(() => {
    if (!props.access?.enable_show_visible_marks) {
      setToolTipMessageVisible("Click to run testcases");
    } else if (!props.access?.enable_show_hidden_marks) {
      setToolTipMessageHidden("Click to run testcases");
    }
  }, [
    props.access?.enable_show_visible_marks,
    props.access?.enable_show_hidden_marks,
  ]);

  const runTestCaseButton = async (value) => {
    if (!props.selectedSubmission?.id) {
      return null;
    }
    // run hidden and visible test case function
    props.setIsLoading(true); //loading spinner component called
    log.log("selected id", props?.selectedSubmission?.id);
    const url = constants.base_url + "/evalpro/evaluate/" + value + "/" + props.selectedSubmission.id;
    let form_data = new FormData();

    try {
      const response = await httpService.put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      //setting test results
      props.setTestResults(response.data.testcase_details);
      //setting access control variables
      props.changeAccess(response.data.access_control_vars);
      props.setIsLoading(false); //loading spinner component called off
      props.setIsSubmissionChanged(false);
      setTestCaseMessage(
        response.data.passed_testcases_count +
        "/" +
        response.data.testcase_details.length +
        " " +
        value +
        " testcases passed"
      );
      if (value === "hidden") {
        setToolTipMessageHidden(
          response.data.passed_testcases_count +
          "/" +
          response.data.testcase_details.length +
          " " +
          " testcases passed"
        )
      } else if (value === "visible") {
        setToolTipMessageVisible(
          response.data.passed_testcases_count +
          "/" +
          response.data.testcase_details.length +
          " " +
          " testcases passed"
        )
      }
    } catch (error) {
      props.setIsLoading(false); //loading spinner component called off
      log.log(error);
    }
  };

  return (
    <div>
      {/* container to hold action buttons */}
      <div className="container-fluid my-2">
        {
          props.selectedSubmission != null &&
          <div className="row text-center">
            <div className="col-md-6">
              {
                props.access?.enable_run_visible_testcases && (
                  <OverlayTrigger
                    id="visible_testcases_diff"
                    overlay={
                      <Tooltip>{"see diff of practice testcases"}</Tooltip>
                    }
                  >
                    <Button
                      id="visible_testcase_diff"
                      variant="outline-primary"
                      disabled={props.isLoading} //button disabled while loading true
                      onClick={() => { setVisibleTestCaseModal(true) }}
                    >
                      Practice Testcases Compare Output
                    </Button>
                  </OverlayTrigger>
                )
              }
            </div>
            <div className="col-md-6">
              {
                props.acccess?.enable_run_hidden_testcases && (
                  <OverlayTrigger
                    id="hidden_testcases_diff"
                    overlay={
                      <Tooltip>{"see diff of evaluation testcases"}</Tooltip>
                    }
                  >
                    <Button
                      id="visible_testcase_diff"
                      //onClick={(e) => runTestCaseButton("visible")}
                      variant="outline-primary"
                      disabled={props.isLoading} //button disabled while loading true
                      onClick={() => { setHiddenTestCaseModal(true) }}
                    >
                      Evaluation Testcases Compare Output
                    </Button>
                  </OverlayTrigger>
                )
              }
            </div>
          </div>
        }
      </div>
      {/* Old table impl for buttons */}
      {/* <Table borderless>
        <thead>
          {
            (!visibleTestcaseExecutionPending || !hiddenTestcaseExecutionPending) ? <tr>
              <th>
                {visibleTestcaseExecutionPending === true ? null : (
                  <>
                    {props.access?.enable_display_visible_testcases &&
                      props.access?.enable_show_visible_marks ? (
                      <>
                        <OverlayTrigger
                          id="run_visible_testcases"
                          overlay={
                            <Tooltip>{toolTipMessageVisible || " "}</Tooltip>
                          } //button disabled while loading true
                        >
                          <Button
                            ref={props.practiceMarks}
                            id="practice_marks"
                            onClick={(e) => runTestCaseButton("visible")}
                            variant="primary"
                            disabled={props.isLoading} //button disabled while loading true
                            style={{ marginRight: "30px", marginBottom: "20px", fontSize: "14px" }}
                          >
                            Run Practice Testcases
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          id="visible_testcases_diff"
                          overlay={
                            <Tooltip>{"see diff of practice testcases"}</Tooltip>
                          }
                        >
                          <Button
                            id="visible_testcase_diff"
                            variant="primary"
                            disabled={props.isLoading} //button disabled while loading true
                            style={{ marginRight: "30px", marginBottom: "20px", fontSize: "14px" }}
                            onClick={() => { setVisibleTestCaseModal(true) }}
                          >
                            Practice Testcases Compare Output
                          </Button>
                        </OverlayTrigger>
                      </>
                    ) : null}
                  </>
                )}

                {hiddenTestcaseExecutionPending === true ? null : (
                  <>
                    {props.access?.enable_display_hidden_testcases &&
                      props.access?.enable_show_hidden_marks ? (
                      <>
                        <OverlayTrigger
                          id="run_hidden_testcases"
                          overlay={
                            <Tooltip>{toolTipMessageHidden || " "}</Tooltip>
                          }
                        >
                          <Button
                            ref={props.evaluateMarks}
                            id="evaluate_marks"
                            onClick={(e) => runTestCaseButton("hidden")}
                            variant="primary"
                            disabled={props.isLoading} //button disabled while loading true
                            style={{ marginBottom: "20px", marginRight: "30px", fontSize: "14px" }}
                          >
                            Run Evaluation Testcases
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          id="hidden_testcases_diff"
                          overlay={
                            <Tooltip>{"see diff of evaluation testcases"}</Tooltip>
                          }
                        >
                          <Button
                            id="visible_testcase_diff"
                            //onClick={(e) => runTestCaseButton("visible")}
                            variant="primary"
                            disabled={props.isLoading} //button disabled while loading true
                            style={{ marginRight: "30px", marginBottom: "20px", fontSize: "14px" }}
                            onClick={() => { setHiddenTestCaseModal(true) }}
                          >
                            Evaluation Testcases Compare Output
                          </Button>
                        </OverlayTrigger>
                      </>
                    ) : null}
                  </>
                )}


              </th>
            </tr> : <></>
          }
        </thead>
        <tbody>
          <tr>
            {props.access?.enable_show_visible_marks ||
              props.access?.enable_show_hidden_marks ? (
              <td style={{ color: "red" }}>{testCaseMessage}</td>
            ) : null}
          </tr>
        </tbody>
      </Table> */}
      {visibleTestCaseModal &&
        <TestCaseModal
          showModal={visibleTestCaseModal}
          setShowModal={setVisibleTestCaseModal}
          studentName={studentName || "student"}
          studentEmail={studentEmail || "student@example.com"}
          obtainedMarks={props.selectedSubmission?.obtained_visible_marks}
          submissionID={props.selectedSubmission?.id}
          testcaseType={"visible_testcases"}
        />
      }
      {hiddenTestCaseModal &&
        <TestCaseModal
          showModal={hiddenTestCaseModal}
          setShowModal={setHiddenTestCaseModal}
          studentName={studentName || "student"}
          studentEmail={studentEmail || "student@example.com"}
          obtainedMarks={props.selectedSubmission?.obtained_hidden_marks}
          submissionID={props.selectedSubmission?.id}
          testcaseType={"hidden_testcases"}
        />
      }
    </div>
  );
}

RunTestcases.defaultProps = {
  selectedSubmission: {},
  setIsLoading: () => { },
  setTestResults: () => { },
  setIsSubmissionChanged: () => { },
  changeAccess: () => { },
  access: {},
  isLoading: false,
  programId: 0,
  delegate_student_id: null,
};

// export default withRouter(connect(mapStateToProps)(RunTestcases));
export default RunTestcases;
